import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
    FormHelperText,
    TextField,
    Box,
    Popover,
    Popper,
    Dialog,
    DialogContent,
    Typography,
    DialogActions,
    Button
} from '@mui/material';
import { useField } from 'formik';
import { useContext, useEffect, useState } from 'react';
// Our Components
import { AuthContext } from 'context/auth-context';
import { Businesses, Cards } from 'lib/api';
import { GET_CARD_TRANSACTIONS } from 'context/transactions/transactionsTypes';
import { TransactionsContext } from 'context/transactions/transactionsContext';
// Assets
import { ReactComponent as AlertIconRed } from 'assets/AlertIconRed.svg';
import { ReactComponent as EditIcon } from 'assets/shared/EditIcon.svg';
import { ReactComponent as TrashIcon } from 'assets/transactionsSection/TrashIcon.svg';
import { updateCardTransaction } from 'lib/api/cards';
// Helpers
import { isAdminUser } from 'helpers/auth';

import './TransactionsCategoryInput.css';

const filter = createFilterOptions();

export const TransactionsCategoryInput = ({
    variant,
    size,
    transaction,
    transactionsCategories,
    transactionsCategoriesRaw,
    parentComponentName,
    isHovered,
    show,
    ...props
}) => {
    const [categoryCardholderObject, setCategoryCardholderObject] = useState(null);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [totalCardTransactionByCategory, setTotalCardTransactionByCategory] = useState(0);
    const { businessInfo, userInfo } = useContext(AuthContext);
    const { dispatch } = useContext(TransactionsContext);
    const [field, meta, helpers] = useField(props);
    const { name, onBlur } = field;
    const { touched, error } = meta;
    const { setValue } = helpers;

    useEffect(() => {
        setCategoryCardholderObject(
            transactionsCategoriesRaw?.find(item => item.id === transaction?.id_business_transaction_category)
        );
    }, [transaction?.id_business_transaction_category, transactionsCategoriesRaw]);

    const handleChange = async (event, newValue) => {
        const categorySelectedObject = transactionsCategoriesRaw.find(item => item.name === newValue?.value);

        if (!newValue) {
            return setValue('');
        }
        if (typeof newValue === 'string') {
            return setValue(newValue?.value);
        }
        if (newValue && newValue?.inputValue) {
            // Create a new value from the user input
            try {
                const newTransactionCategories = await Businesses.createTransactionsCategories(
                    businessInfo.id,
                    newValue.inputValue
                );
                await updateCardTransaction(transaction.id, {
                    id_business_transaction_category: newTransactionCategories[newTransactionCategories.length - 1].id
                });
                dispatch({ type: GET_CARD_TRANSACTIONS });
                return setValue(newValue.inputValue);
            } catch (error) {
                console.error(error);
            }
        }
        try {
            await updateCardTransaction(transaction.id, { id_business_transaction_category: categorySelectedObject.id });
            dispatch({ type: GET_CARD_TRANSACTIONS });
            return setValue(newValue?.value);
        } catch (error) {
            console.error(error);
        }
    };

    const handleFilterOptions = (options, params) => {
        const filtered = filter(options, params);
        const { inputValue } = params;
        // Suggest the creation of a new value
        const isExisting = options.some(option => inputValue === option.value);
        if (inputValue !== '' && !isExisting && isAdminUser(userInfo?.role?.id)) {
            filtered.push({
                inputValue,
                value: `${inputValue} (Add Category)`
            });
        }

        return filtered;
    };

    const handleGetOptionsLabel = option => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
            return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
            return option.inputValue;
        }
        // Regular options
        return option.value;
    };

    const PopperOptions = function (props) {
        return <Popper {...props} style={styles.popper} placement="bottom-start" />;
    };

    const styles = theme => ({
        popper: {
            width: 'fit-content'
        }
    });

    const handleDeleteOption = async () => {
        const categorySelectedObject = transactionsCategoriesRaw.find(item => item.name === selectedOption);

        try {
            await Businesses.deleteCardTransactionCategory(businessInfo.id, categorySelectedObject.id);
            dispatch({ type: GET_CARD_TRANSACTIONS });
            setIsConfirmationDialogOpen(false);
        } catch (error) {
            console.error(error);
        }
    };

    const handleRemove = async () => {
        try {
            const removedCategory = await Cards.removeCardTransactionCategory(transaction?.id);
            dispatch({ type: GET_CARD_TRANSACTIONS });
            return removedCategory;
        } catch (error) {
            console.error(error);
        }
    };

    const getTotalCardTransactionsByCategory = async value => {
        try {
            const categorySelectedObject = transactionsCategoriesRaw.find(item => item.name === value);
            const total = await Businesses.getNumberCardTransactionWithCategory(businessInfo?.id, categorySelectedObject?.id);
            setTotalCardTransactionByCategory(total);
            return total;
        } catch (error) {
            console.error(error);
        }
    };

    const handleTrashIconClick = async optionValue => {
        setSelectedOption(optionValue);
        await getTotalCardTransactionsByCategory(optionValue);
        setIsConfirmationDialogOpen(true);
    };

    return (
        <>
            <Box
                className="transactions-category__input-wrapper"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignContent: 'center',
                    flexWrap: 'nowrap',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                <Autocomplete
                    clearOnBlur
                    sx={{ flex: 1 }}
                    filterOptions={handleFilterOptions}
                    freeSolo
                    getOptionLabel={handleGetOptionsLabel}
                    handleHomeEndKeys
                    PopperComponent={PopperOptions}
                    onChange={(event, newValue, reason) => {
                        if (reason === 'clear') {
                            handleRemove();
                        } else {
                            handleChange(event, newValue);
                        }
                    }}
                    options={transactionsCategories}
                    renderOption={(props, option) => (
                        <li {...props} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{option.value}</span>
                            <span style={{ marginLeft: '20px' }}>
                                {isAdminUser(userInfo?.role?.id) ? (
                                    <TrashIcon
                                        onClick={event => {
                                            event.stopPropagation();
                                            handleTrashIconClick(option.value);
                                        }}
                                    />
                                ) : (
                                    ''
                                )}
                            </span>
                        </li>
                    )}
                    renderPopover={(props, option) => (
                        <Popover
                            {...props}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    width: '300px'
                                }
                            }}>
                            {props.children}
                        </Popover>
                    )}
                    selectOnFocus
                    renderInput={params => (
                        <TextField
                            {...params}
                            name={name}
                            variant={variant}
                            size={size}
                            error={touched && error}
                            onBlur={onBlur}
                            placeholder={!categoryCardholderObject?.name ? '+ Add' : ''}
                            sx={{
                                '& fieldset': { border: parentComponentName === 'TransitionRow' ? 'none' : '' },
                                '& input::placeholder': { opacity: 1, color: transaction?.balance ? 'black' : '#182d55' },
                                backgroundColor: isHovered ? '#EFF1F2' : ''
                            }}
                        />
                    )}
                    value={categoryCardholderObject?.name ? categoryCardholderObject.name : ''}
                />
                {show && <EditIcon />}
            </Box>
            {touched && error && <FormHelperText error>{error}</FormHelperText>}

            <Dialog open={isConfirmationDialogOpen} onClose={() => {}}>
                <DialogContent className="transaction-details__receipt-alert" sx={{ padding: '32px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <Box sx={{ width: '50px', height: '50px', marginTop: '5px' }}>
                            <AlertIconRed />
                        </Box>
                        <Box sx={{ display: 'inline-block', marginLeft: '20px' }}>
                            <Typography className="form-title">
                                Are you sure you want to delete{' '}
                                {selectedOption?.length > 30 && selectedOption.indexOf(' ') === -1
                                    ? selectedOption.substring(0, 24) + '...' // Add suspensive dots if no space and length exceeds 24
                                    : selectedOption}{' '}
                                category?
                            </Typography>
                            <Typography className="text-secondary">
                                Deleting this category will remove it permanently from your list and from{' '}
                                {totalCardTransactionByCategory}{' '}
                                {totalCardTransactionByCategory === 1 ? 'transaction' : 'transactions'} tagged with it.{' '}
                            </Typography>
                        </Box>
                    </Box>
                    <DialogActions sx={{ marginTop: '32px', padding: '0px' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setIsConfirmationDialogOpen(false)}
                            sx={{ color: '#7D95A5', borderColor: '#7D95A5' }}>
                            Close
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleDeleteOption}
                            sx={{
                                backgroundColor: '#F65570',
                                borderColor: '#7D95A5',
                                '&:hover': {
                                    backgroundColor: '#f20d33'
                                }
                            }}>
                            Delete
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TransactionsCategoryInput;
